import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag, Row, Col,
  Drawer, Form, message, InputNumber, Input, DatePicker, Popconfirm,
  Grid, Empty, Spin, Dropdown,
} from 'antd'
import { SendOutlined, PlusOutlined, EditOutlined, MoreOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const { useBreakpoint } = Grid

const NUM_STYLE = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' as const }
const API = ''

const STATUS_COLORS: Record<string, string> = {
  PLANNED: 'default',
  LOADING: 'processing',
  LOADED: 'blue',
  DISPATCHED: 'success',
  DELIVERED: 'cyan',
  CANCELLED: 'error',
}

export default function DispatchPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const screens = useBreakpoint()
  const isMobile = !screens.lg
  const qc = useQueryClient()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingDispatch, setEditingDispatch] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [form] = Form.useForm()

  const { data: dispatches, isLoading } = useQuery({
    queryKey: ['dispatch-orders', plantId],
    queryFn: () =>
      apiClient.get(`${API}/dispatch-orders`, { params: { plant_id: plantId, limit: 200 } }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: confirmedSOs } = useQuery({
    queryKey: ['sales-orders-confirmed', plantId],
    queryFn: () =>
      apiClient.get(`${API}/sales-orders`, {
        params: { plant_id: plantId, status: 'CONFIRMED,PARTIALLY_READY,READY', limit: 200 },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: fgLots } = useQuery({
    queryKey: ['fg-lots-in-store', plantId],
    queryFn: () =>
      apiClient.get(`${API}/fg-lots`, { params: { plant_id: plantId, status: 'IN_STORE', limit: 500 } }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post(`${API}/dispatch-orders`, { ...data, plant_id: plantId }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatch-orders'] })
      message.success('Dispatch order created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`${API}/dispatch-orders/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatch-orders'] })
      message.success('Dispatch updated')
      setDrawerOpen(false)
      setEditingDispatch(null)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const confirmMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`${API}/dispatch-orders/${id}/confirm`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatch-orders'] })
      message.success('Dispatch confirmed')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const openCreate = () => {
    setEditingDispatch(null)
    form.resetFields()
    form.setFieldsValue({ dispatch_date: dayjs() })
    setDrawerOpen(true)
  }

  const openEdit = (d: any) => {
    setEditingDispatch(d)
    form.setFieldsValue({
      ...d,
      dispatch_date: d.dispatch_date ? dayjs(d.dispatch_date) : dayjs(),
    })
    setDrawerOpen(true)
  }

  const handleSave = () => {
    form.validateFields().then(vals => {
      const payload = {
        ...vals,
        dispatch_date: vals.dispatch_date?.format('YYYY-MM-DD'),
      }
      if (editingDispatch) {
        updateMut.mutate({ id: editingDispatch.id, data: payload })
      } else {
        createMut.mutate(payload)
      }
    })
  }

  const dispatchList = Array.isArray(dispatches) ? dispatches : []
  const soList = Array.isArray(confirmedSOs) ? confirmedSOs : []
  const fgList = Array.isArray(fgLots) ? fgLots : []

  const expandedRowRender = (record: any) => {
    const lines = Array.isArray(record.lines) ? record.lines : []
    return (
      <Table
        size="small"
        dataSource={lines}
        rowKey="id"
        pagination={false}
        columns={[
          {
            title: 'FG Lot No', dataIndex: 'fg_lot_no', key: 'fg_lot_no',
            render: (v: string) => <b style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</b>,
          },
          { title: 'Count', dataIndex: 'count_ne', key: 'count_ne', width: 80 },
          { title: 'Cartons', dataIndex: 'carton_count', key: 'carton_count', width: 80, align: 'right' as const },
          { title: 'Cones', dataIndex: 'cone_count', key: 'cone_count', width: 80, align: 'right' as const },
          {
            title: 'Weight (kg)', dataIndex: 'weight_kg', key: 'weight_kg', width: 110, align: 'right' as const,
            render: (v: number) => Number(v).toFixed(3),
          },
        ]}
      />
    )
  }

  const columns = [
    {
      title: 'Dispatch No', dataIndex: 'dispatch_no', key: 'dispatch_no', width: 140,
      render: (v: string) => <b style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</b>,
    },
    {
      title: 'Date', dataIndex: 'dispatch_date', key: 'dispatch_date', width: 110,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—',
    },
    {
      title: 'SO No', key: 'so_no', width: 120,
      render: (_: unknown, r: any) => r.so_no ?? r.sales_order?.so_no ?? '—',
    },
    {
      title: 'Customer', key: 'customer', width: 160,
      render: (_: unknown, r: any) => r.customer_name ?? r.sales_order?.customer_name ?? '—',
    },
    { title: 'Vehicle', dataIndex: 'vehicle_no', key: 'vehicle_no', width: 110 },
    {
      title: 'Total Wt (kg)', dataIndex: 'total_weight_kg', key: 'total_weight_kg', width: 120, align: 'right' as const,
      render: (v: number) => v != null ? Number(v).toFixed(3) : '—',
    },
    {
      title: 'Total Value (₹)', dataIndex: 'total_value', key: 'total_value', width: 130, align: 'right' as const,
      render: (v: number) => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—',
    },
    { title: 'Challan No', dataIndex: 'challan_no', key: 'challan_no', width: 110 },
    { title: 'Invoice No', dataIndex: 'invoice_no', key: 'invoice_no', width: 110 },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v?.replace('_', ' ')}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 160,
      render: (_: unknown, r: any) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          {r.status === 'LOADED' && (
            <Popconfirm title="Confirm this dispatch?" onConfirm={() => confirmMut.mutate(r.id)}>
              <Button size="small" type="primary">Confirm Dispatch</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <SendOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Dispatch Orders
      </Title>
      <Typography.Text type="secondary">Pick from FG store and ship to customers</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
          <Col>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 180 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col flex="auto" />
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Dispatch
            </Button>
          </Col>
        </Row>

        {isMobile ? (
          isLoading ? <Spin /> :
          (!dispatchList || dispatchList.length === 0) ? <Empty description="No dispatches" /> :
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {dispatchList.map((r: any) => {
              const overflow: any[] = [
                { key: 'edit', icon: <EditOutlined />, label: 'Edit', onClick: () => openEdit(r) },
              ]
              if (r.status === 'LOADED') {
                overflow.push({ key: 'confirm',
                  icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                  label: 'Confirm Dispatch', onClick: () => confirmMut.mutate(r.id) })
              }
              return (
                <Card key={r.id} size="small" hoverable
                  styles={{ body: { padding: 14 } }}>
                  <Row align="middle" justify="space-between" wrap={false} gutter={8}>
                    <Col flex="auto" style={{ minWidth: 0 }}>
                      <Space size={6} wrap>
                        <Text strong style={{ fontFamily: 'monospace', fontSize: 13, ...NUM_STYLE }}>
                          {r.dispatch_no}
                        </Text>
                        <Tag color={STATUS_COLORS[r.status] ?? 'default'}
                             style={{ fontSize: 10, margin: 0 }}>
                          {r.status?.replace('_', ' ')}
                        </Tag>
                      </Space>
                    </Col>
                    <Col flex="none">
                      <Dropdown menu={{ items: overflow }} trigger={['click']}>
                        <Button type="text" icon={<MoreOutlined />} style={{ width: 44, height: 44 }} />
                      </Dropdown>
                    </Col>
                  </Row>

                  <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
                    {r.dispatch_date ? dayjs(r.dispatch_date).format('DD MMM YYYY') : '—'}
                    {(r.so_no || r.sales_order?.so_no) && (
                      <span style={{ marginLeft: 8, fontFamily: 'monospace' }}>
                        · SO {r.so_no ?? r.sales_order?.so_no}
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop: 6, fontWeight: 500, overflow: 'hidden',
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.customer_name ?? r.sales_order?.customer_name ?? '—'}
                  </div>

                  {r.vehicle_no && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
                      🚛 <span style={NUM_STYLE}>{r.vehicle_no}</span>
                    </div>
                  )}

                  <Row gutter={8} style={{ marginTop: 10 }}>
                    <Col span={12}>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>Total Wt</div>
                      <div style={{ ...NUM_STYLE, fontWeight: 600 }}>
                        {r.total_weight_kg != null ? `${Number(r.total_weight_kg).toFixed(2)} kg` : '—'}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>Total Value</div>
                      <div style={{ ...NUM_STYLE, fontWeight: 600 }}>
                        {r.total_value != null
                          ? `₹${Number(r.total_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                          : '—'}
                      </div>
                    </Col>
                  </Row>

                  {(r.challan_no || r.invoice_no) && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#8c8c8c' }}>
                      {r.challan_no && <span>Challan: <span style={NUM_STYLE}>{r.challan_no}</span></span>}
                      {r.invoice_no && <span style={{ marginLeft: 8 }}>Invoice: <span style={NUM_STYLE}>{r.invoice_no}</span></span>}
                    </div>
                  )}
                </Card>
              )
            })}
          </Space>
        ) : (
        <Table
          dataSource={dispatchList}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          expandable={{ expandedRowRender }}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          scroll={{ x: 1400 }}
        />
        )}
      </Card>

      <Drawer
        title={editingDispatch ? `Edit Dispatch: ${editingDispatch.dispatch_no}` : 'New Dispatch Order'}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingDispatch(null); form.resetFields() }}
        width={560}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setDrawerOpen(false); setEditingDispatch(null); form.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending || updateMut.isPending}
                onClick={handleSave}
              >
                Save
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="so_id" label="Sales Order" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select confirmed SO"
              options={soList.map((s: any) => ({
                value: s.id,
                label: `${s.so_no} — ${s.customer_name ?? s.customer?.customer_name ?? ''} (${s.quantity_kg}kg)`,
              }))}
            />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="dispatch_date" label="Dispatch Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="vehicle_no" label="Vehicle No">
                <Input placeholder="e.g. MH-12-AB-1234" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="driver_name" label="Driver Name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="transporter" label="Transporter">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="lr_no" label="LR No (Lorry Receipt)">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remarks" label="Remarks">
            <Input />
          </Form.Item>

          {/* Dispatch Lines */}
          <Typography.Text strong>Dispatch Lines</Typography.Text>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Card
                    key={key}
                    size="small"
                    style={{ marginTop: 8 }}
                    styles={{ body: { padding: 8 } }}
                  >
                    <Row gutter={8} align="middle">
                      <Col span={20}>
                        <Form.Item name={[name, 'fg_lot_id']} label="FG Lot" style={{ marginBottom: 4 }}>
                          <Select
                            showSearch
                            optionFilterProp="label"
                            size="small"
                            options={fgList.map((f: any) => ({
                              value: f.id,
                              label: `${f.fg_lot_no} — ${f.count_ne}s ${f.count_description ?? ''} (${Number(f.net_weight_kg ?? 0).toFixed(0)}kg)`,
                            }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Button size="small" danger onClick={() => remove(name)}>Del</Button>
                      </Col>
                    </Row>
                    <Row gutter={8}>
                      <Col span={8}>
                        <Form.Item name={[name, 'weight_kg']} label="Weight (kg)" style={{ marginBottom: 0 }}>
                          <InputNumber placeholder="kg" size="small" style={{ width: '100%' }} min={0} precision={3} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name={[name, 'carton_count']} label="Cartons" style={{ marginBottom: 0 }}>
                          <InputNumber size="small" style={{ width: '100%' }} min={0} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name={[name, 'cone_count']} label="Cones" style={{ marginBottom: 0 }}>
                          <InputNumber size="small" style={{ width: '100%' }} min={0} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  size="small"
                  block
                  style={{ marginTop: 8 }}
                  icon={<PlusOutlined />}
                >
                  Add Line
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Drawer>
    </div>
  )
}
